import "./styles.css";

import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { assetManifest } from "./asset-manifest";
import { ModelSession } from "./viewer/model-session";
import type { ModelViewerManifest, ViewerSession } from "./viewer/types";

// Per-relic editorial data + accent (ala Bootcamp palette)
interface RelicInfo {
  key: string;
  label: string;
  name: string;
  desc: string;
  accent: string;
  soundUrl?: string; // filled after Mint audio generation
}

const RELICS: RelicInfo[] = [
  {
    key: "ai_agent",
    label: "AI",
    name: "Relik Hermes AI Agent",
    desc: "Inti kristal AI yang melayani otomatisasi — bangun agen, rangkai workflow, dan layani pelanggan 24/7.",
    accent: "#3da9ff",
  },
  {
    key: "forex_xau",
    label: "MECH",
    name: "Relik Mech All-Terrain",
    desc: "Mech penjaga edukasi — simbol ketahanan belajar: dari dasar hingga mahir, langkah demi langkah.",
    accent: "#d9a441",
  },
  {
    key: "crypto",
    label: "WEB3",
    name: "Relik Crypto Research",
    desc: "Monolit blockchain amethyst — riset project Web3, baca peluang airdrop, dapatkan token gratis.",
    accent: "#a16bff",
  },
  {
    key: "bundle",
    label: "ALL",
    name: "Relik Bundle",
    desc: "Peti harta edukasi — akses semua camp, tools gratis, mentoring, dan komunitas seumur hidup.",
    accent: "#3fbf8f",
  },
  {
    key: "robot_hermes",
    label: "BOT·AI",
    name: "Robot Hermes",
    desc: "Agen humanoid yang merangkai workflow dan menjawab pelanggan secara otomatis — wujud nyata AI Agent.",
    accent: "#3da9ff",
  },
  {
    key: "robot_crypto",
    label: "BOT·WEB3",
    name: "Drone Crypto",
    desc: "Drone penjelajah on-chain yang memetakan peluang airdrop dan riset project Web3 secara real-time.",
    accent: "#a16bff",
  },
  {
    key: "robot_content",
    label: "BOT·MKT",
    name: "Bot Content AI",
    desc: "Robot pembuat konten: copywriting, ide video, dan iklan otomatis untuk UMKM dan kreator.",
    accent: "#d9a441",
  },
  {
    key: "robot_umkm",
    label: "BOT·UMKM",
    name: "Bot UMKM",
    desc: "Asisten roda empat yang membantu operasional bisnis harian — kasir, stok, dan layanan pelanggan.",
    accent: "#3fbf8f",
  },
  {
    key: "robot_mech",
    label: "BOT·MECH",
    name: "Mech Sentinel",
    desc: "Mech penjaga terdepan — pelindung komunitas dan simbol fokus saat berlatih di musim bootcamp.",
    accent: "#8fd0ff",
  },
];

function requiredElement<T extends Element>(selector: string): T {
  const el = document.querySelector<T>(selector);
  if (!el) throw new Error(`Missing element: ${selector}`);
  return el;
}

// ---- tiny WebAudio click fallback (no external file) ----
let audioCtx: AudioContext | null = null;
function playClick(accent: string) {
  try {
    audioCtx ??= new (window.AudioContext || (window as any).webkitAudioContext)();
    const ctx = audioCtx;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const color = new THREE.Color(accent);
    const freq = 320 + color.r * 220 + color.g * 120;
    osc.type = "sine";
    osc.frequency.setValueAtTime(freq, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(freq * 1.5, ctx.currentTime + 0.08);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.18);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.2);
  } catch {
    /* ignore */
  }
}

// Optional Mint-generated ambience/selection audio
async function playMintSound(url?: string) {
  if (!url) return;
  try {
    const a = new Audio(url);
    await a.play();
  } catch {
    /* ignore */
  }
}

class RelikApp {
  private readonly app = requiredElement<HTMLElement>("#app");
  private readonly canvas = requiredElement<HTMLCanvasElement>("#viewer-canvas");
  private readonly loadingSurface = requiredElement<HTMLElement>("#loading-surface");
  private readonly loadingLabel = requiredElement<HTMLElement>("#loading-label");
  private readonly statusLine = requiredElement<HTMLElement>("#status-line");
  private readonly swatchWrap = requiredElement<HTMLElement>("#relik-swatches");
  private readonly nameEl = requiredElement<HTMLElement>("#relik-name");
  private readonly descEl = requiredElement<HTMLElement>("#relik-desc");
  private readonly copyEl = requiredElement<HTMLElement>(".relik-copy");
  private readonly ctaEl = requiredElement<HTMLButtonElement>("#relik-cta");
  private readonly resetBtn = requiredElement<HTMLButtonElement>("#reset-view");

  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(46, 1, 0.01, 1000);
  private readonly controls: OrbitControls;
  private readonly manifest: ModelViewerManifest;
  private session: ViewerSession | null = null;
  private modelSession: ModelSession | null = null;
  private loadVersion = 0;
  private lastTime = performance.now();

  constructor(manifest: ModelViewerManifest) {
    this.manifest = manifest;
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));

    this.controls = new OrbitControls(this.camera, this.canvas);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.minDistance = 1.8;
    this.controls.maxDistance = 9;
    this.controls.autoRotate = true;
    this.controls.autoRotateSpeed = 0.6;

    this.scene.add(new THREE.HemisphereLight(0xffffff, 0xece7dd, 1.7));
    const key = new THREE.DirectionalLight(0xffffff, 2.4);
    key.position.set(4, 7, 5);
    this.scene.add(key);
    const rim = new THREE.DirectionalLight(0xb8d2ff, 0.9);
    rim.position.set(-5, 3, -4);
    this.scene.add(rim);

    new ResizeObserver(() => this.resize()).observe(this.app);
    this.resize();

    this.buildSwatches();
    this.ctaEl.addEventListener("click", () => {
      // Bootcamp rule: any admin/WA contact opens QRIS gate first.
      const target = "https://bootcamp.web.id/#camp";
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ type: "relik-open-qris" }, "*");
      } else {
        window.location.href = target;
      }
    });
    this.resetBtn.addEventListener("click", () => this.session?.resetView());

    requestAnimationFrame(this.renderFrame);
  }

  private buildSwatches() {
    RELICS.forEach((r, i) => {
      const b = document.createElement("button");
      b.className = "relik-swatch";
      b.type = "button";
      b.setAttribute("role", "tab");
      b.setAttribute("aria-selected", String(i === 0));
      b.dataset.index = String(i);
      b.innerHTML = `<span class="dot" style="background:${r.accent}"></span><span class="lbl">${r.label}</span>`;
      b.addEventListener("click", () => this.select(i));
      this.swatchWrap.appendChild(b);
    });
  }

  private setAccent(color: string) {
    document.documentElement.style.setProperty("--relik-accent", color);
  }

  start() {
    this.select(0);
  }

  private select(index: number) {
    const info = RELICS[index];
    this.setAccent(info.accent);
    this.nameEl.textContent = info.name;
    this.descEl.textContent = info.desc;
    this.copyEl.style.borderColor = `color-mix(in srgb, ${info.accent} 30%, transparent)`;
    playClick(info.accent);
    void playMintSound(info.soundUrl);

    this.swatchWrap.querySelectorAll<HTMLButtonElement>(".relik-swatch").forEach((b, i) =>
      b.setAttribute("aria-selected", String(i === index)),
    );
    void this.loadItem(index);
  }

  private async loadItem(index: number) {
    const item = this.manifest.items[index];
    if (!item) return;
    const version = ++this.loadVersion;
    this.session?.dispose();
    this.session = null;
    this.modelSession = null;
    this.setLoading(true, `Memuat ${item.title}…`);

    try {
      const session = await ModelSession.create({
        scene: this.scene,
        camera: this.camera,
        controls: this.controls,
        item,
        callbacks: {
          onStats: () => {},
          onAnimations: () => {},
          onAnimationProgress: () => {},
        },
      });
      if (version !== this.loadVersion) {
        session.dispose();
        return;
      }
      this.session = session;
      this.modelSession = session;
      this.setLoading(false);
      this.statusLine.textContent = "Drag untuk orbit · Scroll untuk zoom";
    } catch (err) {
      if (version !== this.loadVersion) return;
      this.loadingLabel.textContent =
        err instanceof Error ? err.message : "Relik tidak dapat dimuat.";
    }
  }

  private setLoading(on: boolean, label?: string) {
    if (label) this.loadingLabel.textContent = label;
    this.loadingSurface.hidden = !on;
  }

  private resize() {
    const w = this.app.clientWidth;
    const h = this.app.clientHeight;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / Math.max(1, h);
    this.camera.updateProjectionMatrix();
  }

  private readonly renderFrame = () => {
    requestAnimationFrame(this.renderFrame);
    const now = performance.now();
    const dt = Math.min(0.05, (now - this.lastTime) / 1000);
    this.lastTime = now;
    this.controls.update();
    this.modelSession?.update(dt);
    this.renderer.render(this.scene, this.camera);
  };
}

new RelikApp(assetManifest).start();
